import { useMutation } from '@apollo/client';
import { CLIENT_UPDATE_FEMALE_BIO, CLIENT_UPDATE_ALLERGIES, CLIENT_UPDATE_BIO, CLIENT_UPDATE_BLOODLINE_DISEASES, CLIENT_UPDATE_CONTACT_DETAILS, CLIENT_UPDATE_OTHER_MEDICATIONS, CLIENT_UPDATE_SPECIALISTS, CLIENT_UPDATE_VACCINATIONS, CLIENT_UPDATE_SURGERIES, CLIENT_UPDATE_IMPORTANT_NOTES } from '../../services/graphql/mutations/client/index';
import { useCallback } from 'react';

const buildFemaleBioParams = (values: any) => {
    const femaleBio = values?.clientBioFemale || values || {};
    const params: Record<string, any> = {};

    if (femaleBio.firstMensAge !== undefined && femaleBio.firstMensAge !== null && femaleBio.firstMensAge !== '') {
        const parsedAge = Number(femaleBio.firstMensAge);
        if (!Number.isNaN(parsedAge)) {
            params.firstMensAge = parsedAge;
        }
    }

    if (femaleBio.isInMenopause !== undefined && femaleBio.isInMenopause !== null) {
        if (typeof femaleBio.isInMenopause === 'string') {
            params.isInMenopause = femaleBio.isInMenopause.toLowerCase() === 'true';
        } else {
            params.isInMenopause = Boolean(femaleBio.isInMenopause);
        }
    }

    return params;
};

export const useUpdateClientFemaleBio = () => {
    const [updateClientFemaleBio, { loading }] = useMutation(CLIENT_UPDATE_FEMALE_BIO);

    const updateData = useCallback(async (values: any) => {
        try {
            const params = buildFemaleBioParams(values);
            const { data } = await updateClientFemaleBio({
                variables: {
                    input: {
                        params
                    }
                }
            });
            return data;
        } catch (error) {
            console.log('Error in updateClientFemaleBio: ', error);
            throw error;
        }
    }, [updateClientFemaleBio]);

    return {
        update: updateData,
        loading
    };
};

export const useUpdateClientBio = () => {
    const [updateClientBio, { loading }] = useMutation(CLIENT_UPDATE_BIO);

    const updateData = useCallback(async (values: any) => {
        try {
            const { data } = await updateClientBio({
                variables: {
                    input: {
                        params: {
                            ...(values.firstName && { firstName: values.firstName }),
                            ...(values.familyName && { familyName: values.familyName }),
                            bioAttributes: {
                                ...(values.sex && { sex: values.sex }),
                                ...(values.weight && { weight: values.weight }),
                                ...(values.waistCircumference && { waistCircumference: values.waistCircumference }),
                                ...(values.isVegetarian !== undefined && { isVegetarian: values.isVegetarian }),
                                ...(values.dateOfBirth && { dateOfBirth: values.dateOfBirth }),
                                ...(values.length && { length: values.length }),
                            },
                        }
                    },
                }
            });
            return data;
        } catch (error) {
            console.log('Error in updateClientBio: ', error);
            throw error;
        }
    }, [updateClientBio]);

    return {
        update: updateData,
        loading
    };
};

export const useUpdateClientContactDetails = () => {
    const [updateClientContactDetails, { loading }] = useMutation(CLIENT_UPDATE_CONTACT_DETAILS);

    const updateData = useCallback(async (values: any) => {
        try {
            const { data } = await updateClientContactDetails({
                variables: {
                    familyDetailAttributes: values.familyDetailAttributes,
                    careerAndEducationDetailAttributes: values.careerAndEducationDetailAttributes,
                    personalContactDetailAttributes: {
                        ...values.personalContactDetailAttributes,
                        telephoneCountryCode: values.personalContactDetailAttributes?.telephoneCountryCode || ' '
                    },
                    caregiverContactDetailsAttributes: values.caregiverContactDetailsAttributes || []
                }
            });
            return data;
        } catch (error) {
            console.log('Error in updateClientContactDetails: ', error);
            throw error;
        }
    }, [updateClientContactDetails]);

    return {
        update: updateData,
        loading
    };
};

export const useUpdateClientAllergies = () => {
    const [updateClientAllergies, { loading }] = useMutation(CLIENT_UPDATE_ALLERGIES);

    const updateData = useCallback(async (values: any) => {
        try {
            const { data } = await updateClientAllergies({
                variables: {
                    input: {
                        params: {
                            ...values
                        }
                    },
                }
            });
            return data;
        } catch (error) {
            console.log('Error in updateClientAllergies: ', error);
            throw error;
        }
    }, [updateClientAllergies]);

    return {
        update: updateData,
        loading
    };
};

export const useUpdateClientSpecialists = () => {
    const [updateClientSpecialists, { loading }] = useMutation(CLIENT_UPDATE_SPECIALISTS);

    const updateData = useCallback(async (values: any) => {
        try {
            const { data } = await updateClientSpecialists({
                variables: {
                    input: {
                        specialistsAttributes: {
                            id: values?.id,
                            name: values?.name,
                            address: values?.address,
                            telephoneCountryCode: values?.telephoneCountryCode,
                            telephoneNumber: values?.telephoneNumber,
                            forWhat: values?.forWhat,
                            sinceWhen: values?.sinceWhen,
                            _destroy: values?._destroy
                        },
                        otherHealthProfessionalsAttributes: values?.otherSpecialists || []
                    },
                }
            });
            return data;
        } catch (error) {
            console.log('Error in updateClientSpecialists: ', error);
            throw error;
        }
    }, [updateClientSpecialists]);

    return {
        update: updateData,
        loading
    };
};

export const useUpdateClientOtherMedications = () => {
    const [updateClientOtherMedications, { loading }] = useMutation(CLIENT_UPDATE_OTHER_MEDICATIONS);

    const updateData = useCallback(async (values: any) => {
        try {
            const { data } = await updateClientOtherMedications({
                variables: {
                    input: {
                        params: {
                            ...values
                        }
                    },
                }
            });
            return data;
        } catch (error) {
            console.log('Error in updateClientOtherMedications: ', error);
            throw error;
        }
    }, [updateClientOtherMedications]);

    return {
        update: updateData,
        loading
    };
};

export const useUpdateClientVaccinations = () => {
    const [updateClientVaccinations, { loading }] = useMutation(CLIENT_UPDATE_VACCINATIONS);

    const updateData = useCallback(async (values: any[]) => {
        try {
            const { data } = await updateClientVaccinations({
                variables: {
                    input: {
                        params: values,
                    },
                }
            });
            return data;
        } catch (error) {
            console.log('Error in updateClientVaccinations: ', error);
            throw error;
        }
    }, [updateClientVaccinations]);

    return {
        update: updateData,
        loading
    };
};

export const useUpdateClientBloodlineDiseases = () => {
    const [updateClientBloodlineDiseases, { loading }] = useMutation(CLIENT_UPDATE_BLOODLINE_DISEASES);

    const updateData = useCallback(async (values: any[]) => {
        try {
            const { data } = await updateClientBloodlineDiseases({
                variables: {
                    input: {
                        params: values,
                    },
                }
            });
            return data;
        } catch (error) {
            console.log('Error in updateClientBloodlineDiseases: ', error);
            throw error;
        }
    }, [updateClientBloodlineDiseases]);

    return {
        update: updateData,
        loading
    };
};

export const useUpdateClientSurgeries = () => {
    const [updateClientSurgeries, { loading }] = useMutation(CLIENT_UPDATE_SURGERIES);

    const updateData = useCallback(async (values: any[]) => {
        try {
            const { data } = await updateClientSurgeries({
                variables: {
                    input: {
                        params: values,
                    },
                }
            });
            return data;
        } catch (error) {
            console.log('Error in updateClientSurgeries: ', error);
            throw error;
        }
    }, [updateClientSurgeries]);

    return {
        update: updateData,
        loading
    };
};

export const useUpdateClientImportantNotes = () => {
    const [updateClientImportantNotes, { loading }] = useMutation(CLIENT_UPDATE_IMPORTANT_NOTES);

    const updateData = useCallback(async (values: any[]) => {
        try {
            const { data } = await updateClientImportantNotes({
                variables: {
                    input: {
                        params: values,
                    },
                }
            });
            return data;
        } catch (error) {
            console.log('Error in updateClientImportantNotes: ', error);
            throw error;
        }
    }, [updateClientImportantNotes]);

    return {
        update: updateData,
        loading
    };
};
